import React, { useState, useMemo, useCallback } from 'react';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RemoveIcon from '@mui/icons-material/Remove';
import { ChainPicker, Chain, loadChain, saveChain } from '../../components/common/ChainPicker';
import { useStaggeredEntrance } from '../../hooks/useStaggeredEntrance';

type TimeView = 'weekly' | 'monthly' | 'yearly' | 'all';
type CoverageMode = 'crops' | 'stores';

const TIME_VIEWS: { key: TimeView; label: string }[] = [
  { key: 'weekly', label: 'Weekly' },
  { key: 'monthly', label: 'Monthly' },
  { key: 'yearly', label: 'Yearly' },
  { key: 'all', label: 'All Time' },
];

function getTimeConfig(tv: TimeView): { points: number; labels: string[]; periodLabel: string } {
  switch (tv) {
    case 'weekly':
      return { points: 7, labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'], periodLabel: 'this week' };
    case 'monthly':
      return {
        points: 30,
        labels: Array.from({ length: 30 }, (_, i) => `${i + 1}`),
        periodLabel: 'this month',
      };
    case 'yearly':
      return { points: 52, labels: Array.from({ length: 52 }, (_, i) => `W${i + 1}`), periodLabel: 'this year' };
    case 'all': {
      const l: string[] = [];
      for (let y = 23; y <= 26; y++) for (let q = 1; q <= 4; q++) l.push(`Q${q} '${y}`);
      return { points: l.length, labels: l, periodLabel: 'all time' };
    }
  }
}

function generateSeries(count: number, start: number, end: number, noise: number, seed: number): number[] {
  const step = (end - start) / (count - 1);
  return Array.from({ length: count }, (_, i) => {
    const base = start + step * i;
    const t = i / (count - 1); // 0..1
    const j =
      Math.sin(i * 2.7 + seed * 13.1) * 0.3 +
      Math.cos(i * 1.4 + seed * 7.3) * 0.25 +
      Math.sin(i * 5.3 + seed * 3.7) * 0.2 +
      Math.cos(i * 8.1 + seed * 11.9) * 0.15 +
      Math.sin(i * 12.6 + seed * 5.2) * 0.1 +
      Math.sin(t * Math.PI * 2 + seed * 4.1) * 0.15 +
      Math.cos(t * Math.PI * 6 + seed * 9.4) * 0.1;
    return Math.round((base + j * noise) * 1000) / 1000;
  });
}

function chainSeed(chain: string): number {
  let h = 0;
  for (let i = 0; i < chain.length; i++) h = ((h << 5) - h + chain.charCodeAt(i)) | 0;
  return (Math.abs(h) % 1000) / 1000;
}

function rangeStart(tv: TimeView, current: number, yearlyStart: number): number {
  const diff = current - yearlyStart;
  switch (tv) {
    case 'weekly': return current - diff * 0.1;
    case 'monthly': return current - diff * 0.35;
    case 'yearly': return yearlyStart;
    case 'all': return yearlyStart - diff * 0.5;
  }
}

// ===== Chart geometry =====
const SVG_W = 600;
const SVG_H = 200;
const PAD = { top: 10, right: 14, bottom: 28, left: 52 };

function cxFn(i: number, count: number, padR = PAD.right): number {
  return PAD.left + (i / (count - 1)) * (SVG_W - PAD.left - padR);
}
function cyFn(val: number, min: number, max: number): number {
  const range = max - min || 1;
  return PAD.top + (1 - (val - min) / range) * (SVG_H - PAD.top - PAD.bottom);
}

function toPolyline(data: number[], min: number, max: number, padR = PAD.right): string {
  return data.map((v, i) => `${cxFn(i, data.length, padR)},${cyFn(v, min, max)}`).join(' ');
}

function toAreaPath(data: number[], min: number, max: number, padR = PAD.right): string {
  const coords = data.map((v, i) => ({ x: cxFn(i, data.length, padR), y: cyFn(v, min, max) }));
  const line = coords.map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x},${c.y}`).join(' ');
  const bottom = SVG_H - PAD.bottom;
  return `${line} L${coords[coords.length - 1].x},${bottom} L${coords[0].x},${bottom} Z`;
}

function toBandPath(hi: number[], lo: number[], min: number, max: number, padR = PAD.right): string {
  const hiCoords = hi.map((v, i) => ({ x: cxFn(i, hi.length, padR), y: cyFn(v, min, max) }));
  const loCoords = lo.map((v, i) => ({ x: cxFn(i, lo.length, padR), y: cyFn(v, min, max) }));
  const fwd = hiCoords.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  const bwd = [...loCoords].reverse().map((p) => `L${p.x},${p.y}`).join(' ');
  return `${fwd} ${bwd} Z`;
}

function dataDots(data: number[], min: number, max: number, color: string, padR = PAD.right): React.ReactNode {
  return data.map((v, i) => (
    <circle key={i} cx={cxFn(i, data.length, padR)} cy={cyFn(v, min, max)} r={data.length > 20 ? 1.5 : 2} fill={color} />
  ));
}

function extent(...series: number[][]): [number, number] {
  const all = series.flat();
  const min = Math.min(...all);
  const max = Math.max(...all);
  const pad = (max - min) * 0.05;
  return [min - pad, max + pad];
}

// ===== Axes =====
const ChartAxes: React.FC<{
  min: number; max: number; labels: string[];
  format: (v: number) => string; tickCount?: number;
  rightMin?: number; rightMax?: number; rightFormat?: (v: number) => string; rightColor?: string;
  padRight?: number;
}> = ({ min, max, labels, format, tickCount = 4, rightMin, rightMax, rightFormat, rightColor, padRight }) => {
  const yTicks = Array.from({ length: tickCount }, (_, i) => min + (max - min) * (i / (tickCount - 1)));
  const step = labels.length > 8 ? Math.ceil(labels.length / 6) : 1;
  const rEdge = padRight != null ? SVG_W - padRight : SVG_W - PAD.right;
  const hasRight = rightMin != null && rightMax != null && rightFormat;
  const rTicks = hasRight ? Array.from({ length: tickCount }, (_, i) => rightMin + (rightMax - rightMin) * (i / (tickCount - 1))) : [];
  return (
    <>
      {yTicks.map((val, i) => {
        const y = cyFn(val, min, max);
        return (
          <g key={i}>
            <line x1={PAD.left} x2={rEdge} y1={y} y2={y} stroke="var(--border)" strokeWidth="0.5" strokeDasharray="4 2" />
            <text x={PAD.left - 8} y={y + 3.5} textAnchor="end" fill="var(--text-tertiary)" fontSize="10" fontFamily="var(--font-mono)">
              {format(val)}
            </text>
          </g>
        );
      })}
      {hasRight && rTicks.map((val, i) => {
        const y = cyFn(val, rightMin, rightMax);
        return (
          <text key={`r${i}`} x={rEdge + 8} y={y + 3.5} textAnchor="start" fill={rightColor || 'var(--text-tertiary)'} fontSize="10" fontFamily="var(--font-mono)">
            {rightFormat(val)}
          </text>
        );
      })}
      {labels.map((label, i) =>
        i % step === 0 ? (
          <text key={i} x={cxFn(i, labels.length, padRight || PAD.right)} y={SVG_H - 6} textAnchor="middle" fill="var(--text-tertiary)" fontSize="10">
            {label}
          </text>
        ) : null,
      )}
      <line x1={PAD.left} x2={rEdge} y1={SVG_H - PAD.bottom} y2={SVG_H - PAD.bottom} stroke="var(--border)" strokeWidth="1" />
      <line x1={PAD.left} x2={PAD.left} y1={PAD.top} y2={SVG_H - PAD.bottom} stroke="var(--border)" strokeWidth="1" />
      {hasRight && <line x1={rEdge} x2={rEdge} y1={PAD.top} y2={SVG_H - PAD.bottom} stroke={rightColor || 'var(--border)'} strokeWidth="1" opacity="0.4" />}
    </>
  );
};

// ===== Interactive hover chart =====
interface HoverSeries {
  data: number[];
  color: string;
  label: string;
  min: number;
  max: number;
  format: (v: number) => string;
}

const HoverChart: React.FC<{
  series: HoverSeries[];
  labels: string[];
  children: React.ReactNode;
  padR?: number;
}> = ({ series, labels, children, padR = PAD.right }) => {
  const [hover, setHover] = useState<{ idx: number; x: number } | null>(null);
  const count = series[0]?.data.length || 0;

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    const svgX = pct * SVG_W;
    if (svgX < PAD.left - 5 || svgX > SVG_W - padR + 5) {
      setHover(null);
      return;
    }
    let closest = 0;
    let closestDist = Infinity;
    for (let i = 0; i < count; i++) {
      const dist = Math.abs(svgX - cxFn(i, count, padR));
      if (dist < closestDist) { closestDist = dist; closest = i; }
    }
    setHover({ idx: closest, x: e.clientX - rect.left });
  }, [count, padR]);

  const handleMouseLeave = useCallback(() => setHover(null), []);

  // Flip tooltip to left side if near right edge
  const tooltipOnLeft = hover !== null && hover.x > 200;

  return (
    <div style={{ position: 'relative' }} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
      <svg viewBox={`0 0 ${SVG_W} ${SVG_H}`} style={{ width: '100%', height: 200, display: 'block', cursor: 'crosshair' }}>
        {children}
        {hover !== null && (
          <>
            <line
              x1={cxFn(hover.idx, count, padR)} x2={cxFn(hover.idx, count, padR)}
              y1={PAD.top} y2={SVG_H - PAD.bottom}
              stroke="var(--text-tertiary)" strokeWidth="1" strokeDasharray="3 2" opacity="0.6"
            />
            {series.map((s, i) => (
              <circle
                key={i}
                cx={cxFn(hover.idx, count, padR)}
                cy={cyFn(s.data[hover.idx], s.min, s.max)}
                r={4}
                fill={s.color}
                stroke="var(--surface)"
                strokeWidth="2"
              />
            ))}
          </>
        )}
      </svg>
      {hover !== null && (
        <div style={{
          position: 'absolute',
          top: 4,
          left: tooltipOnLeft ? undefined : hover.x + 12,
          right: tooltipOnLeft ? undefined : undefined,
          ...(tooltipOnLeft ? { left: hover.x - 12, transform: 'translateX(-100%)' } : {}),
          pointerEvents: 'none',
          background: 'var(--elevated)',
          border: '1px solid var(--border)',
          borderRadius: 6,
          padding: '8px 12px',
          fontSize: 12,
          zIndex: 10,
          whiteSpace: 'nowrap',
          boxShadow: 'var(--shadow-elevated)',
        }}>
          <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--text-primary)' }}>
            {labels[hover.idx]}
          </div>
          {series.map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
              <span style={{ color: 'var(--text-secondary)' }}>{s.label}</span>
              <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-primary)', marginLeft: 8 }}>
                {s.format(s.data[hover.idx])}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ===== Presentational components =====
const ChartCard: React.FC<{
  title: string; subtitle: string; children: React.ReactNode;
  isVisible: boolean; headerRight?: React.ReactNode;
}> = ({ title, subtitle, children, isVisible, headerRight }) => (
  <div
    style={{
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(8px)',
      transition: 'opacity 350ms ease, transform 350ms ease',
      marginBottom: 20,
    }}
  >
    <div className="forge-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div className="forge-card-title">{title}</div>
          <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16, marginTop: -4 }}>{subtitle}</div>
        </div>
        {headerRight}
      </div>
      {children}
    </div>
  </div>
);

const TrendBadge: React.FC<{ value: string; positive: boolean }> = ({ value, positive }) => (
  <span
    style={{
      display: 'inline-flex', alignItems: 'center', gap: 2,
      fontSize: 13, fontFamily: 'var(--font-mono)',
      color: positive ? 'var(--positive)' : 'var(--danger)',
    }}
  >
    {positive ? <TrendingUpIcon sx={{ fontSize: 16 }} /> : <TrendingDownIcon sx={{ fontSize: 16 }} />}
    {value}
  </span>
);

const ModeToggle: React.FC<{ value: CoverageMode; onChange: (m: CoverageMode) => void }> = ({ value, onChange }) => (
  <div style={{ display: 'inline-flex', borderRadius: 4, overflow: 'hidden', border: '1px solid var(--border)' }}>
    {(['crops', 'stores'] as const).map((m, i) => (
      <button
        key={m}
        onClick={() => onChange(m)}
        style={{
          padding: '3px 10px', fontSize: 11, fontWeight: 500, border: 'none', cursor: 'pointer',
          background: value === m ? 'var(--accent)' : 'var(--surface)',
          color: value === m ? '#fff' : 'var(--text-secondary)',
          borderRight: i === 0 ? '1px solid var(--border)' : 'none',
          transition: 'background 150ms, color 150ms',
        }}
      >
        {m === 'crops' ? 'Crops' : 'Unique Stores'}
      </button>
    ))}
  </div>
);

const StatsRow: React.FC<{ children: React.ReactNode; trend?: React.ReactNode }> = ({ children, trend }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>{children}</div>
    {trend && <div>{trend}</div>}
  </div>
);

const StatValue: React.FC<{ value: string; label: string; color: string }> = ({ value, label, color }) => (
  <div>
    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 22, fontWeight: 700, color }}>{value}</span>
    <span style={{ fontSize: 12, color: 'var(--text-tertiary)', marginLeft: 6 }}>{label}</span>
  </div>
);

const Legend: React.FC<{ items: { color: string; label: string; dashed?: boolean }[] }> = ({ items }) => (
  <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
    {items.map((it) => (
      <span key={it.label} style={{ fontSize: 12, color: it.color }}>
        {it.dashed ? '- -' : '\u2014'} {it.label}
      </span>
    ))}
  </div>
);

// Format helpers
const fmtPct = (v: number) => v.toFixed(0) + '%';
const fmtPct1 = (v: number) => v.toFixed(1) + '%';
const fmtDec3 = (v: number) => v.toFixed(3);
const fmtCount = (v: number) => v >= 1000 ? (v / 1000).toFixed(1) + 'k' : v.toFixed(0);
const fmtInt = (v: number) => Math.round(v).toLocaleString();

// ===== Main component =====
export const AggregatedDashboard: React.FC = () => {
  const [chain, setChain] = useState<Chain>(loadChain);
  const [timeView, setTimeView] = useState<TimeView>('monthly');
  const [upcMode, setUpcMode] = useState<CoverageMode>('crops');
  const [varMode, setVarMode] = useState<CoverageMode>('crops');
  const visibility = useStaggeredEntrance(8, 60);

  const handleChainChange = (c: Chain) => { setChain(c); saveChain(c); };

  const tc = getTimeConfig(timeView);
  const seed = chainSeed(chain);
  const cf = 0.97 + seed * 0.06;

  const data = useMemo(() => {
    const n = tc.points;
    const s = seed;

    // UPC Coverage — by crops
    const upcC1 = generateSeries(n, rangeStart(timeView, 91.5 * cf, 82 * cf), 91.5 * cf, 4.0, s);
    const upcC5 = generateSeries(n, rangeStart(timeView, 78.3 * cf, 65 * cf), 78.3 * cf, 5.0, s + 0.1);
    const upcC15 = generateSeries(n, rangeStart(timeView, 52.4 * cf, 38 * cf), 52.4 * cf, 6.0, s + 0.2);
    // UPC Coverage — by unique stores
    const upcS1 = generateSeries(n, rangeStart(timeView, 88.2 * cf, 76 * cf), 88.2 * cf, 4.5, s + 0.15);
    const upcS5 = generateSeries(n, rangeStart(timeView, 61.7 * cf, 48 * cf), 61.7 * cf, 5.5, s + 0.25);
    const upcS15 = generateSeries(n, rangeStart(timeView, 34.8 * cf, 21 * cf), 34.8 * cf, 6.5, s + 0.35);

    // Design Coverage — by crops
    const varC1 = generateSeries(n, rangeStart(timeView, 94.1 * cf, 85 * cf), 94.1 * cf, 3.5, s + 1.0);
    const varC5 = generateSeries(n, rangeStart(timeView, 72.8 * cf, 58 * cf), 72.8 * cf, 5.0, s + 1.1);
    const varC15 = generateSeries(n, rangeStart(timeView, 41.3 * cf, 27 * cf), 41.3 * cf, 6.0, s + 1.2);
    // Design Coverage — by unique stores
    const varS1 = generateSeries(n, rangeStart(timeView, 91.2 * cf, 80 * cf), 91.2 * cf, 4.0, s + 1.15);
    const varS5 = generateSeries(n, rangeStart(timeView, 58.4 * cf, 43 * cf), 58.4 * cf, 5.5, s + 1.25);
    const varS15 = generateSeries(n, rangeStart(timeView, 28.6 * cf, 15 * cf), 28.6 * cf, 6.5, s + 1.35);

    // Design Assignment
    const varAssign = generateSeries(n, rangeStart(timeView, 78.3 * cf, 61 * cf), 78.3 * cf, 4.0, s + 0.3);

    // Data Quality
    const dupEnd = Math.round(335 / cf);
    const duplicates = generateSeries(n, rangeStart(timeView, dupEnd, Math.round(520 / cf)), dupEnd, 30, s + 0.4);
    const cohEnd = 0.847 * cf;
    const cohesion = generateSeries(n, rangeStart(timeView, cohEnd, 0.79 * cf), cohEnd, 0.02, s + 0.5);

    // Inference Confidence
    const confEnd = 0.891 * cf;
    const confidence = generateSeries(n, rangeStart(timeView, confEnd, 0.872 * cf), confEnd, 0.012, s + 0.6);
    const bandSize = timeView === 'weekly' ? 0.004 : timeView === 'monthly' ? 0.008 : timeView === 'yearly' ? 0.012 : 0.015;
    const confHi = confidence.map((v, i) => v + bandSize + Math.sin(i * 1.7 + s * 5) * 0.005);
    const confLo = confidence.map((v, i) => v - bandSize - Math.sin(i * 1.7 + s * 5) * 0.005);
    // Avg top1-top2 delta (higher = better discrimination)
    const deltaEnd = 0.142 * cf;
    const confDelta = generateSeries(n, rangeStart(timeView, deltaEnd, 0.118 * cf), deltaEnd, 0.012, s + 0.65);

    // Crop Volume
    const cropEnd = Math.round(48293 * cf);
    const crops = generateSeries(n, rangeStart(timeView, cropEnd, Math.round(38200 * cf)), cropEnd, 1800, s + 0.7);

    return {
      upcC1, upcC5, upcC15, upcS1, upcS5, upcS15,
      varC1, varC5, varC15, varS1, varS5, varS15,
      varAssign, duplicates, cohesion, confidence, confHi, confLo, confDelta, crops,
    };
  }, [timeView, tc.points, seed, cf]);

  const last = <T,>(arr: T[]) => arr[arr.length - 1];
  const first = <T,>(arr: T[]) => arr[0];
  const delta = (arr: number[]) => last(arr) - first(arr);
  const trendStr = (d: number, unit: string) => `${d >= 0 ? '+' : ''}${d.toFixed(1)}${unit} ${tc.periodLabel}`;
  const trendStr3 = (d: number) => `${d >= 0 ? '+' : ''}${d.toFixed(3)} ${tc.periodLabel}`;

  // Active coverage series — fixed 0-100% range
  const upc1 = upcMode === 'crops' ? data.upcC1 : data.upcS1;
  const upc5 = upcMode === 'crops' ? data.upcC5 : data.upcS5;
  const upc15 = upcMode === 'crops' ? data.upcC15 : data.upcS15;

  const var1 = varMode === 'crops' ? data.varC1 : data.varS1;
  const var5 = varMode === 'crops' ? data.varC5 : data.varS5;
  const var15 = varMode === 'crops' ? data.varC15 : data.varS15;

  const [vaMin, vaMax] = extent(data.varAssign);
  const [cohMin, cohMax] = extent(data.cohesion);
  const dupMin = Math.min(...data.duplicates) * 0.95;
  const dupMax = Math.max(...data.duplicates) * 1.05;
  const [confMin, confMax] = extent(data.confHi, data.confLo);
  const [deltaMin, deltaMax] = extent(data.confDelta);
  const [cropMin, cropMax] = extent(data.crops);

  const btnGroupStyle: React.CSSProperties = {
    display: 'inline-flex', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--border)',
  };
  const btnBase: React.CSSProperties = {
    padding: '6px 14px', fontSize: 13, fontWeight: 500, border: 'none',
    cursor: 'pointer', transition: 'background 150ms, color 150ms', borderRight: '1px solid var(--border)',
  };

  const coverageLabel = (mode: CoverageMode) => mode === 'crops' ? 'crops' : 'unique stores';

  return (
    <div className="page-content">
      {/* Top bar */}
      <div
        style={{
          opacity: visibility[0] ? 1 : 0,
          transform: visibility[0] ? 'translateY(0)' : 'translateY(8px)',
          transition: 'opacity 350ms ease, transform 350ms ease',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexWrap: 'wrap', gap: 12, marginBottom: 20,
        }}
      >
        <ChainPicker value={chain} onChange={handleChainChange} />
        <div style={btnGroupStyle}>
          {TIME_VIEWS.map((tv, i) => (
            <button
              key={tv.key}
              onClick={() => setTimeView(tv.key)}
              style={{
                ...btnBase,
                background: timeView === tv.key ? 'var(--accent)' : 'var(--surface)',
                color: timeView === tv.key ? '#fff' : 'var(--text-secondary)',
                ...(i === TIME_VIEWS.length - 1 ? { borderRight: 'none' } : {}),
              }}
            >
              {tv.label}
            </button>
          ))}
        </div>
      </div>

      {/* UPC Coverage */}
      <ChartCard
        title="UPC Coverage"
        subtitle={`% of whitelisted UPCs with N+ ${coverageLabel(upcMode)}`}
        isVisible={visibility[1]}
        headerRight={<ModeToggle value={upcMode} onChange={setUpcMode} />}
      >
        <StatsRow trend={<TrendBadge value={trendStr(delta(upc1), '%')} positive={delta(upc1) >= 0} />}>
          <StatValue value={`${last(upc1).toFixed(1)}%`} label={`\u2265 1`} color="var(--warning)" />
          <StatValue value={`${last(upc5).toFixed(1)}%`} label={`\u2265 5`} color="#E3873D" />
          <StatValue value={`${last(upc15).toFixed(1)}%`} label={`\u2265 15`} color="var(--positive)" />
        </StatsRow>
        <HoverChart
          series={[
            { data: upc1, color: 'var(--warning)', label: '\u2265 1', min: 0, max: 100, format: fmtPct1 },
            { data: upc5, color: '#E3873D', label: '\u2265 5', min: 0, max: 100, format: fmtPct1 },
            { data: upc15, color: 'var(--positive)', label: '\u2265 15', min: 0, max: 100, format: fmtPct1 },
          ]}
          labels={tc.labels}
        >
          <ChartAxes min={0} max={100} labels={tc.labels} format={fmtPct} tickCount={5} />
          <polyline points={toPolyline(upc15, 0, 100)} fill="none" stroke="var(--positive)" strokeWidth="1.5" strokeLinejoin="round" />
          <polyline points={toPolyline(upc5, 0, 100)} fill="none" stroke="#E3873D" strokeWidth="1.5" strokeLinejoin="round" />
          <polyline points={toPolyline(upc1, 0, 100)} fill="none" stroke="var(--warning)" strokeWidth="1.5" strokeLinejoin="round" />
          {dataDots(upc15, 0, 100, 'var(--positive)')}
          {dataDots(upc5, 0, 100, '#E3873D')}
          {dataDots(upc1, 0, 100, 'var(--warning)')}
        </HoverChart>
        <Legend items={[
          { color: 'var(--warning)', label: '\u2265 1' },
          { color: '#E3873D', label: '\u2265 5' },
          { color: 'var(--positive)', label: '\u2265 15' },
        ]} />
      </ChartCard>

      {/* Design Coverage */}
      <ChartCard
        title="Design Coverage"
        subtitle={`% of designs with N+ ${coverageLabel(varMode)}`}
        isVisible={visibility[2]}
        headerRight={<ModeToggle value={varMode} onChange={setVarMode} />}
      >
        <StatsRow trend={<TrendBadge value={trendStr(delta(var1), '%')} positive={delta(var1) >= 0} />}>
          <StatValue value={`${last(var1).toFixed(1)}%`} label={`\u2265 1`} color="var(--warning)" />
          <StatValue value={`${last(var5).toFixed(1)}%`} label={`\u2265 5`} color="#E3873D" />
          <StatValue value={`${last(var15).toFixed(1)}%`} label={`\u2265 15`} color="var(--positive)" />
        </StatsRow>
        <HoverChart
          series={[
            { data: var1, color: 'var(--warning)', label: '\u2265 1', min: 0, max: 100, format: fmtPct1 },
            { data: var5, color: '#E3873D', label: '\u2265 5', min: 0, max: 100, format: fmtPct1 },
            { data: var15, color: 'var(--positive)', label: '\u2265 15', min: 0, max: 100, format: fmtPct1 },
          ]}
          labels={tc.labels}
        >
          <ChartAxes min={0} max={100} labels={tc.labels} format={fmtPct} tickCount={5} />
          <polyline points={toPolyline(var15, 0, 100)} fill="none" stroke="var(--positive)" strokeWidth="1.5" strokeLinejoin="round" />
          <polyline points={toPolyline(var5, 0, 100)} fill="none" stroke="#E3873D" strokeWidth="1.5" strokeLinejoin="round" />
          <polyline points={toPolyline(var1, 0, 100)} fill="none" stroke="var(--warning)" strokeWidth="1.5" strokeLinejoin="round" />
          {dataDots(var15, 0, 100, 'var(--positive)')}
          {dataDots(var5, 0, 100, '#E3873D')}
          {dataDots(var1, 0, 100, 'var(--warning)')}
        </HoverChart>
        <Legend items={[
          { color: 'var(--warning)', label: '\u2265 1' },
          { color: '#E3873D', label: '\u2265 5' },
          { color: 'var(--positive)', label: '\u2265 15' },
        ]} />
      </ChartCard>

      {/* Design Assignment */}
      <ChartCard title="Design Assignment" subtitle="% of active product crops with design_id" isVisible={visibility[3]}>
        <StatsRow trend={<TrendBadge value={trendStr(delta(data.varAssign), '%')} positive={delta(data.varAssign) >= 0} />}>
          <StatValue value={`${last(data.varAssign).toFixed(1)}%`} label="current" color="var(--accent)" />
        </StatsRow>
        <HoverChart
          series={[{ data: data.varAssign, color: 'var(--accent)', label: 'Assigned', min: vaMin, max: vaMax, format: fmtPct1 }]}
          labels={tc.labels}
        >
          <ChartAxes min={vaMin} max={vaMax} labels={tc.labels} format={fmtPct} />
          <path d={toAreaPath(data.varAssign, vaMin, vaMax)} fill="var(--accent)" opacity="0.1" />
          <polyline points={toPolyline(data.varAssign, vaMin, vaMax)} fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinejoin="round" />
          {dataDots(data.varAssign, vaMin, vaMax, 'var(--accent)')}
        </HoverChart>
      </ChartCard>

      {/* Data Quality */}
      <ChartCard
        title="Data Quality"
        subtitle="Intra-design duplicate pairs (>96% cosine similarity) & avg design cohesion score"
        isVisible={visibility[4]}
      >
        <StatsRow>
          <div>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 22, fontWeight: 700, color: 'var(--danger)' }}>{Math.round(last(data.duplicates))}</span>
            <span style={{ fontSize: 12, color: 'var(--text-tertiary)', marginLeft: 6 }}>duplicate pairs</span>
            {last(data.duplicates) < first(data.duplicates) && <span style={{ fontSize: 12, color: 'var(--positive)', marginLeft: 6 }}>&darr;</span>}
          </div>
          <div>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 22, fontWeight: 700, color: 'var(--accent)' }}>{last(data.cohesion).toFixed(3)}</span>
            <span style={{ fontSize: 12, color: 'var(--text-tertiary)', marginLeft: 6 }}>avg cohesion</span>
            {last(data.cohesion) > first(data.cohesion) && <span style={{ fontSize: 12, color: 'var(--positive)', marginLeft: 6 }}>&uarr;</span>}
          </div>
        </StatsRow>
        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 10, lineHeight: 1.5 }}>
          <strong style={{ color: 'var(--text-secondary)' }}>Duplicates:</strong> crop pairs within the same design whose embeddings have {'>'} 96% cosine similarity — likely redundant images that inflate design size.{' '}
          <strong style={{ color: 'var(--text-secondary)' }}>Cohesion:</strong> avg pairwise similarity within each design (higher = tighter clusters, lower = possible misassignment).
        </div>
        <HoverChart
          series={[
            { data: data.duplicates, color: 'var(--danger)', label: 'Dup. pairs (>96%)', min: dupMin, max: dupMax, format: fmtInt },
            { data: data.cohesion, color: 'var(--accent)', label: 'Cohesion', min: cohMin, max: cohMax, format: fmtDec3 },
          ]}
          labels={tc.labels}
        >
          <ChartAxes min={cohMin} max={cohMax} labels={tc.labels} format={fmtDec3} />
          <polyline
            points={toPolyline(data.duplicates, dupMin, dupMax)}
            fill="none" stroke="var(--danger)" strokeWidth="1.5" strokeLinejoin="round" strokeDasharray="6 3"
          />
          <polyline points={toPolyline(data.cohesion, cohMin, cohMax)} fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinejoin="round" />
          {dataDots(data.duplicates, dupMin, dupMax, 'var(--danger)')}
          {dataDots(data.cohesion, cohMin, cohMax, 'var(--accent)')}
        </HoverChart>
        <Legend items={[
          { color: 'var(--danger)', label: 'duplicate pairs >96% sim (relative scale)', dashed: true },
          { color: 'var(--accent)', label: 'avg design cohesion' },
        ]} />
      </ChartCard>

      {/* Inference Confidence */}
      <ChartCard title="Inference Confidence" subtitle="Avg top-1 confidence & top1−top2 delta across PC-active stores" isVisible={visibility[5]}>
        <StatsRow trend={
          Math.abs(delta(data.confidence)) < 0.003 ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 13, fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)' }}>
              <RemoveIcon sx={{ fontSize: 14 }} /> &plusmn;{Math.abs(delta(data.confidence)).toFixed(3)} {tc.periodLabel}
            </span>
          ) : (
            <TrendBadge value={trendStr3(delta(data.confidence))} positive={delta(data.confidence) >= 0} />
          )
        }>
          <StatValue value={last(data.confidence).toFixed(3)} label="top-1" color="var(--blue)" />
          <StatValue value={last(data.confDelta).toFixed(3)} label="avg delta" color="var(--purple)" />
        </StatsRow>
        <HoverChart
          series={[
            { data: data.confidence, color: 'var(--blue)', label: 'Top-1', min: confMin, max: confMax, format: fmtDec3 },
            { data: data.confDelta, color: 'var(--purple)', label: 'Avg Delta', min: deltaMin, max: deltaMax, format: fmtDec3 },
          ]}
          labels={tc.labels}
          padR={52}
        >
          <ChartAxes min={confMin} max={confMax} labels={tc.labels} format={fmtDec3} rightMin={deltaMin} rightMax={deltaMax} rightFormat={fmtDec3} rightColor="var(--purple)" padRight={52} />
          <path d={toBandPath(data.confHi, data.confLo, confMin, confMax, 52)} fill="var(--blue)" opacity="0.1" />
          <polyline points={toPolyline(data.confidence, confMin, confMax, 52)} fill="none" stroke="var(--blue)" strokeWidth="1.5" strokeLinejoin="round" />
          <polyline points={toPolyline(data.confDelta, deltaMin, deltaMax, 52)} fill="none" stroke="var(--purple)" strokeWidth="1.5" strokeLinejoin="round" strokeDasharray="6 3" />
          {dataDots(data.confidence, confMin, confMax, 'var(--blue)', 52)}
          {dataDots(data.confDelta, deltaMin, deltaMax, 'var(--purple)', 52)}
        </HoverChart>
        <Legend items={[
          { color: 'var(--blue)', label: 'top-1 confidence' },
          { color: 'var(--purple)', label: 'avg delta (higher = better)', dashed: true },
        ]} />
      </ChartCard>

      {/* Crop Volume */}
      <ChartCard title="Crop Volume" subtitle="Total active crops over time" isVisible={visibility[6]}>
        <StatsRow trend={
          <TrendBadge
            value={`${delta(data.crops) >= 0 ? '+' : ''}${fmtCount(Math.abs(delta(data.crops)))} ${tc.periodLabel}`}
            positive={delta(data.crops) >= 0}
          />
        }>
          <StatValue value={Math.round(last(data.crops)).toLocaleString()} label="current" color="var(--accent)" />
        </StatsRow>
        <HoverChart
          series={[{ data: data.crops, color: 'var(--accent)', label: 'Crops', min: cropMin, max: cropMax, format: fmtInt }]}
          labels={tc.labels}
        >
          <ChartAxes min={cropMin} max={cropMax} labels={tc.labels} format={fmtCount} />
          <path d={toAreaPath(data.crops, cropMin, cropMax)} fill="var(--accent)" opacity="0.12" />
          <polyline points={toPolyline(data.crops, cropMin, cropMax)} fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinejoin="round" />
          {dataDots(data.crops, cropMin, cropMax, 'var(--accent)')}
        </HoverChart>
      </ChartCard>
    </div>
  );
};
